/* /upgrade/success — WildQuest Explorer welcome landing.
 *
 * Reached after Stripe redirects the buyer back with ?session_id=…&r=<return-path>.
 * Polls /api/billing/status/{session_id} until Stripe (or the webhook) marks the
 * transaction "paid", then shows a full-screen celebration and auto-navigates
 * the player back to where they came from after ~4 seconds.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Sparkles, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { trackPurchase } from '../lib/metaPixel';

const AUTO_RETURN_MS = 4200;
const POLL_INTERVAL_MS = 1400;
const POLL_TIMEOUT_MS = 30000;

function Confetti() {
  const pieces = useMemo(() =>
    Array.from({ length: 42 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.9,
      dur: 1.9 + Math.random() * 1.6,
      rot: Math.random() * 720 - 360,
      hue: [45, 145, 200, 320][i % 4],
      size: 6 + Math.random() * 8,
    })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map(p => (
        <motion.span key={p.id}
          initial={{ y: -40, x: `${p.x}%`, rotate: 0, opacity: 0 }}
          animate={{ y: '110vh', rotate: p.rot, opacity: [0, 1, 1, 0] }}
          transition={{ duration: p.dur, delay: p.delay, ease: 'easeIn' }}
          style={{
            position: 'absolute',
            width: p.size, height: p.size * 0.5,
            background: `hsl(${p.hue}, 92%, 62%)`,
            borderRadius: 2,
            boxShadow: `0 0 8px hsl(${p.hue},92%,62%)`,
          }}
        />
      ))}
    </div>
  );
}

export default function UpgradeSuccess() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const rawReturn = params.get('r') || '/dashboard';
  const returnPath = rawReturn.startsWith('/') ? rawReturn : `/${rawReturn}`;

  const [state, setState] = useState('verifying'); // verifying | ready | timeout | error
  const [nickname, setNickname] = useState(null);
  const timerRef = useRef(null);
  const startedRef = useRef(Date.now());

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data?.payment_status === 'paid') {
          setNickname(data.nickname || null);
          setState('ready');
          // Meta Pixel — fire Purchase conversion once, right after
          // Stripe confirms 'paid'. amount_total (in major units) +
          // currency come from the enriched billing/status response;
          // if either is missing we still fire the event so the
          // conversion counter is correct.
          try {
            trackPurchase({
              value: typeof data.amount_total === 'number' ? data.amount_total : undefined,
              currency: (data.currency || 'usd').toUpperCase(),
              contentIds: data.lookup_key ? [data.lookup_key] : [],
              contentCategory: data.lookup_key?.startsWith('pack_') ? 'credit_pack' : 'subscription',
            });
          } catch { /* non-fatal */ }
          // Announce so the CreditsIndicator chip + SubscriptionLayer
          // refetch fresh credits + tier (covers pack + subscription).
          try {
            const { announceCreditsChanged } = await import('../components/CreditsIndicator');
            announceCreditsChanged();
          } catch { /* non-fatal */ }
          return;
        }
      } catch (_err) { /* transient — poll again */ }
      if (Date.now() - startedRef.current > POLL_TIMEOUT_MS) {
        setState('timeout');
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { stopped = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId]);

  useEffect(() => {
    if (state !== 'ready') return;
    const t = setTimeout(() => nav(returnPath, { replace: true }), AUTO_RETURN_MS);
    return () => clearTimeout(t);
  }, [state, nav, returnPath]);

  return (
    <div data-testid="upgrade-success-root"
      className="fixed inset-0 z-[80] bg-[#040A08] text-white flex items-center justify-center overflow-hidden">
      {state === 'ready' && <Confetti />}

      <div className="relative w-full max-w-md px-6 py-8">
        <AnimatePresence mode="wait">
          {state === 'verifying' && (
            <motion.div key="verifying" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="text-center space-y-4" data-testid="upgrade-success-verifying">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-accent" />
              <p className="text-[11px] uppercase tracking-[0.35em] text-white/70">Confirming your payment…</p>
              <p className="text-sm text-white/60">Hold tight — Stripe is finalising your upgrade.</p>
            </motion.div>
          )}

          {state === 'ready' && (
            <motion.div key="ready"
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
              className="relative text-center space-y-5 rounded-3xl border border-amber-400/40 bg-gradient-to-br from-amber-500/15 via-black/70 to-emerald-900/25 backdrop-blur-xl p-8 shadow-[0_0_60px_-6px_rgba(251,191,36,0.55)]"
              data-testid="upgrade-success-ready">
              <motion.div
                initial={{ scale: 0.6, rotate: -20, opacity: 0 }}
                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 120 }}
                className="w-16 h-16 mx-auto rounded-full bg-amber-500/25 border border-amber-400/60 flex items-center justify-center">
                <Crown className="w-8 h-8 text-amber-200" />
              </motion.div>
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase tracking-[0.4em] text-amber-200">Welcome, Explorer</p>
                <h1 className="font-serif text-3xl leading-tight" data-testid="upgrade-success-title">
                  You're now a WildQuest Explorer
                </h1>
                {nickname && (
                  <p className="text-sm text-white/80">
                    Adventure unlocked for <span className="text-accent font-medium">{nickname}</span>.
                  </p>
                )}
              </div>
              <div className="text-[12px] text-white/70 leading-relaxed">
                Unlimited progression, arena, crafting and collections — your journey continues without limits.
              </div>
              <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-amber-200/80">
                <Sparkles className="w-3 h-3" />
                Returning you to your adventure…
              </div>
              <button
                data-testid="upgrade-success-return-btn"
                onClick={() => nav(returnPath, { replace: true })}
                className="mt-1 text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white transition">
                Skip →
              </button>
            </motion.div>
          )}

          {state === 'timeout' && (
            <motion.div key="timeout" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="text-center space-y-3" data-testid="upgrade-success-timeout">
              <p className="font-serif text-2xl">Almost there…</p>
              <p className="text-sm text-white/70">
                Your payment is still processing. It'll update automatically — you can safely continue.
              </p>
              <button data-testid="upgrade-success-continue-btn"
                onClick={() => nav(returnPath, { replace: true })}
                className="mt-2 px-4 py-2 rounded-full bg-accent text-black text-[11px] uppercase tracking-widest">
                Continue
              </button>
            </motion.div>
          )}

          {state === 'error' && (
            <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="text-center space-y-3" data-testid="upgrade-success-error">
              <p className="font-serif text-2xl">Missing session</p>
              <p className="text-sm text-white/70">We couldn't find your checkout session.</p>
              <button data-testid="upgrade-success-error-btn"
                onClick={() => nav('/upgrade', { replace: true })}
                className="mt-2 px-4 py-2 rounded-full bg-accent text-black text-[11px] uppercase tracking-widest">
                Back to upgrade
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
